import type {
  AnalyticsCatalog,
  QueryContext,
  QueryResult,
  WorkerRequest,
  WorkerResponse,
} from "./types";

interface Pending {
  resolve: (result: QueryResult) => void;
  reject: (error: Error) => void;
  malloy: string;
  id: number;
  timer?: ReturnType<typeof setTimeout>;
}
export class QueryService {
  private worker?: Worker;
  private id = 0;
  private queue: Pending[] = [];
  private active?: Pending;
  private failure?: Error;
  private ready!: Promise<AnalyticsCatalog>;
  private readyResolve?: (catalog: AnalyticsCatalog) => void;
  private readyReject?: (reason: Error) => void;
  private initTimer?: ReturnType<typeof setTimeout>;
  constructor(
    private context: QueryContext,
    private createWorker = () =>
      new Worker(new URL("./analytics.worker.ts", import.meta.url), {
        type: "module",
      }),
  ) {
    this.start();
  }
  private start() {
    this.failure = undefined;
    this.ready = new Promise<AnalyticsCatalog>((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
    // Prevent a rejected startup promise becoming an unhandled rejection before a query subscribes.
    void this.ready.catch(() => undefined);
    this.worker = this.createWorker();
    const currentWorker = this.worker;
    this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      if (currentWorker !== this.worker) return;
      const response = event.data;
      if (response.type === "ready") {
        clearTimeout(this.initTimer);
        this.readyResolve?.(response.catalog);
        return;
      }
      const pending = this.active?.id === response.id ? this.active : undefined;
      if (!pending) {
        if (response.type === "error") {
          clearTimeout(this.initTimer);
          this.readyReject?.(new Error(response.error));
        }
        return;
      }
      clearTimeout(pending.timer);
      this.active = undefined;
      if (response.type === "result") pending.resolve(response.result);
      else if (response.type === "error")
        pending.reject(new Error(response.error));
      this.dispatch();
    };
    this.worker.onerror = () => {
      if (currentWorker !== this.worker) return;
      this.restart(
        "The data engine failed. Retry the report on the restarted engine.",
      );
    };
    this.initTimer = setTimeout(
      () =>
        this.fail(
          new Error(
            "The data engine took too long to start. Reload the page to retry.",
          ),
        ),
      30000,
    );
    this.worker.postMessage({
      id: ++this.id,
      type: "init",
      context: this.context,
    } satisfies WorkerRequest);
  }
  private fail(error: Error) {
    clearTimeout(this.initTimer);
    this.failure = error;
    this.readyReject?.(error);
    for (const value of [
      ...(this.active ? [this.active] : []),
      ...this.queue,
    ]) {
      clearTimeout(value.timer);
      value.reject(error);
    }
    this.active = undefined;
    this.queue = [];
  }
  getCatalog() {
    return this.ready;
  }
  async query(malloy: string): Promise<QueryResult> {
    const worker = this.worker;
    await this.ready;
    if (worker !== this.worker || !this.worker)
      throw new Error("Query context changed.");
    if (this.failure) throw this.failure;
    return new Promise((resolve, reject) => {
      this.queue.push({ id: ++this.id, malloy, resolve, reject });
      this.dispatch();
    });
  }
  private dispatch() {
    if (this.active || this.failure || !this.worker) return;
    const pending = this.queue.shift();
    if (!pending) return;
    this.active = pending;
    pending.timer = setTimeout(
      () =>
        this.restart(
          "The query exceeded five seconds. The data engine has been reset; try a smaller query.",
        ),
      5000,
    );
    this.worker.postMessage({
      id: pending.id,
      type: "query",
      malloy: pending.malloy,
    } satisfies WorkerRequest);
  }
  cancel() {
    this.restart(
      "Query cancelled. The data engine is ready for another query.",
    );
  }
  private restart(message: string) {
    this.fail(new Error(message));
    this.worker?.terminate();
    this.start();
  }
  destroy() {
    this.fail(new Error("Query context changed."));
    this.worker?.terminate();
    this.worker = undefined;
  }
}
